import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import {
  useGetAllNotificationsQuery,
  useMarkNotificationAsReadMutation,
} from "../../notifications/api/notificationApi";
import { NotificationResponseType } from "../../notifications/types/notificationResponse";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../../core/data/slice/authSlice";
import { showToast } from "../../../core/utils/alert";

const NotificationActivities = () => {
  const routes = all_routes;

  // Get current user info for center_id
  const currentUser = useSelector(selectCurrentUser);
  const centerId = currentUser?.center_id;

  // Setup mark as read mutation
  const [markAsRead] = useMarkNotificationAsReadMutation();

  // Fetch notifications using API
  const {
    data: notificationsData,
    isLoading,
    isError,
    refetch,
  } = useGetAllNotificationsQuery(
    {
      size: 10, // Get more notifications for the activities page
      page: 1,
      created: false, // Add this to only get notifications NOT created by the current user
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  // Extract notifications from response
  const notifications: NotificationResponseType[] =
    notificationsData?.data?.items || [];

  // Format timestamp to IST
  const formatToIST = (timestamp: string) => {
    if (!timestamp) return "";

    // Parse the timestamp (coming in as UTC)
    const date = new Date(timestamp);

    // Create IST date by adding 5:30 hours to UTC time
    const istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);

    // Calculate time difference in seconds
    const now = new Date();
    const seconds = Math.floor((now.getTime() - istDate.getTime()) / 1000);

    if (seconds < 0) {
      // If seconds is negative, it means the adjusted time is in the future relative to now
      // This can happen due to timezone differences or server/client clock mismatch
      return "Just now";
    }

    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? "s" : ""} ago`;
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
    }

    const days = Math.floor(hours / 24);
    if (days < 7) {
      return `${days} day${days !== 1 ? "s" : ""} ago`;
    }

    // For older notifications, return the formatted date
    return istDate.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Truncate content to a certain length for preview
  const truncateContent = (content: string, maxLength: number = 120) => {
    if (!content) return "";
    return content.length > maxLength
      ? content.substring(0, maxLength) + "..."
      : content;
  };

  // Handle marking a notification as read
  const handleMarkAsRead = async (notificationId: number) => {
    if (!centerId || !notificationId) return;

    try {
      const response = await markAsRead({
        notification_id: notificationId,
        center_id: centerId,
      }).unwrap();

      if (response.success) {
        // Refetch notifications to update the UI
        refetch();
      }
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  // Navigate to notification and mark as read
  const navigateToNotification = (notificationId: number) => {
    // Mark notification as read
    handleMarkAsRead(notificationId);
  };

  // Add the CSS using useEffect
  useEffect(() => {
    // Create a style element
    const styleElement = document.createElement("style");
    styleElement.textContent = `
      .notification-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0.15rem 0.5rem;
        font-size: 0.6rem;
        font-weight: 600;
        line-height: 1;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #fff;
        background: linear-gradient(135deg, #ff416c, #dc3545);
        border-radius: 2px 8px 2px 8px;
        margin-left: 0.7rem;
        box-shadow: 0 2px 8px rgba(220, 53, 69, 0.4);
        position: relative;
        overflow: hidden;
        transform: perspective(100px) rotateX(0deg);
        transform-origin: bottom;
        animation: badge-float 3s ease-in-out infinite, badge-glow 2s ease-in-out infinite;
        font-family: "Brush Script MT", "Brush Script Std", "Lucida Calligraphy", "Lucida Handwriting", "Apple Chancery", cursive;
        font-style: italic;
      }
      
      .notification-badge:before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(45deg, transparent 25%, rgba(255,255,255,0.3) 50%, transparent 75%);
        animation: badge-shimmer 2.5s linear infinite;
        background-size: 200% 200%;
      }
      
      .notification-badge:after {
        content: "";
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        border: 1px solid rgba(255, 255, 255, 0.3);
        border-radius: 2px 8px 2px 8px;
        pointer-events: none;
        animation: badge-border-pulse 2s linear infinite;
      }
      
      @keyframes badge-float {
        0%, 100% { transform: perspective(100px) rotateX(0deg) translateY(0); }
        50% { transform: perspective(100px) rotateX(3deg) translateY(-2px); }
      }
      
      @keyframes badge-shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      
      @keyframes badge-glow {
        0%, 100% { box-shadow: 0 2px 8px rgba(220, 53, 69, 0.4); }
        50% { box-shadow: 0 2px 12px rgba(220, 53, 69, 0.7); }
      }
      
      @keyframes badge-border-pulse {
        0%, 100% { opacity: 0.5; }
        50% { opacity: 1; }
      }
    `;

    // Append it to the document head
    document.head.appendChild(styleElement);

    // Cleanup function to remove the style element when component unmounts
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Notifications</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Notifications
                </li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Activities */}
        <div className="card">
          <div className="card-body pb-1">
            {isLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : isError ? (
              <div className="alert alert-danger" role="alert">
                Failed to load notifications. Please try again later.
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="ti ti-bell-off fs-1 mb-3 d-block"></i>
                <p>No notifications found</p>
              </div>
            ) : (
              <div className="d-block">
                {notifications
                  .filter((notification) => notification.isPublished)
                  .map((notification, index, array) => {
                    // Check if notification has been read based on read_at field
                    const isRead = !!notification.read_at;
                    const hasAttachments =
                      notification.attachments?.filter(
                        (att) => att && att.trim() !== ""
                      ).length > 0;

                    return (
                      <div
                        key={notification.id}
                        className={`${
                          index < array.length - 1
                            ? "border-bottom mb-3 pb-3"
                            : "mb-3"
                        } ${!isRead ? "bg-light-100 rounded p-3" : "p-2"}`}
                      >
                        <Link
                          to={routes.notificationView.replace(
                            ":id",
                            String(notification.id)
                          )}
                          onClick={() =>
                            navigateToNotification(notification.id)
                          }
                          className="text-decoration-none"
                        >
                          <div className="d-flex flex-column">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <div className="d-flex align-items-center flex-wrap">
                                <h5
                                  className={`mb-0 ${
                                    !isRead ? "fw-bold" : "fw-medium"
                                  }`}
                                  style={{ fontSize: "1rem" }}
                                >
                                  {notification.title}
                                </h5>
                                {!isRead && (
                                  <span className="notification-badge">
                                    New
                                  </span>
                                )}
                              </div>
                              <small
                                className="text-muted ms-2 flex-shrink-0"
                                style={{ fontSize: "0.8rem" }}
                              >
                                {formatToIST(notification.publishedOn)}
                              </small>
                            </div>
                            <p
                              className={`text-muted mb-0 ${
                                !isRead ? "fw-medium" : ""
                              }`}
                              style={{ lineHeight: "1.5", fontSize: "0.9rem" }}
                            >
                              {truncateContent(notification.content, 120)}
                            </p>
                            {hasAttachments && (
                              <div className="mt-2">
                                <span
                                  className="text-primary"
                                  style={{ fontSize: "0.8rem" }}
                                >
                                  <i className="ti ti-paperclip me-1"></i>
                                  {
                                    notification.attachments.filter(
                                      (att) => att && att.trim() !== ""
                                    ).length
                                  }{" "}
                                  attachment(s)
                                </span>
                              </div>
                            )}
                          </div>
                        </Link>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
        {/* /Activities */}
      </div>
    </div>
  );
};

export default NotificationActivities;
